import { z } from 'zod';
import { ReactElement, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useWatch } from 'react-hook-form';
import CasFormInput from '@cas/components/inputs/cas-form-input/CasFormInput';
import { ModelName } from '@casops/store/api';
import ModelForm from '@casops/components/layout/model-form/ModelForm';
import useModelForm from '@casops/components/layout/model-form/useModelForm';
import { ServerErrorFields } from '@dmc/ui';
import { ObjectType } from '@casops/store/apis/commonModels';
import {
  RegisteredAssetDetailModel,
  RegisteredAssetFormModel,
  EditRegisteredAssetFormModel,
  useEditAssetMutation,
} from '@casops/store/apis/registeredAssetsApi';
import CasFormMultiselect from '@cas/components/inputs/cas-form-multiselect/CasFormMultiselect';
import useAssetGroupOptions from '@casops/side-panels/manage-geofence/components/general/useAssetGroupOptions';
import { ValidNameRegex } from '@casops/store/apis/commonModels';
import CasFormDropdown from '@cas/components/inputs/cas-form-dropdown/CasFormDropdown';
import styles from './AssetForm.module.scss';
import { useConfigGroupNumberOptions } from '../../useConfigGroupNumberOptions';
import { useObjectTypeOptions } from '@casops/side-panels/filter-panel/filters/object-types/ObjectTypesFilter';
import { AssetDeviceType } from '@casops/store/apis/registeredAssetsApi';
import { useFormEditMode } from '@casops/components/layout/model-form/useFormEditMode';

const DESCRIPTION_MAX = 511;
const NAME_MAX = 8;

type DataModel = RegisteredAssetDetailModel;
type FormModel = RegisteredAssetFormModel;
type EditModel = EditRegisteredAssetFormModel;

const modelName = ModelName.Asset;

const defaultValues: FormModel = {
  n: '',
  cfg: null,
  ags: [],
  obj: ObjectType.Unknown,
  desc: '',
};

const getEditArgs = ({ n, cfg, ags, obj, desc }: FormModel): EditModel => ({
  n,
  cfg,
  ags,
  obj,
  desc,
});

const serverErrorFields: ServerErrorFields<FormModel> = {
  n: 'Name',
  cfg: 'ConfigurationGroupId',
  ags: 'AssetGroupIds',
  obj: 'ObjectType',
  desc: 'Description',
};

interface AssetFormProps {
  item?: DataModel;
}

export const AssetForm = ({ item }: AssetFormProps): ReactElement => {
  const { t } = useTranslation();
  const isPpu = item?.hw === AssetDeviceType.PPU;

  const editMode = useFormEditMode();

  const schema = useMemo(() => {
    return z.object({
      n: z
        .string()
        .min(1, { message: t('Validation.SpecifyAssetName') })
        .max(NAME_MAX, { message: t('Validation.AssetNameTooLong', { max: NAME_MAX }) })
        .regex(ValidNameRegex, { message: t('Validation.InvalidCharacters') }),
      obj: z.nativeEnum(ObjectType, {
        errorMap: () => ({ message: t('Validation.SpecifyObjectType') }),
      }),
      cfg: isPpu
        ? z.number().nullable().optional()
        : z.number({
            errorMap: () => ({ message: t('Validation.SpecifyConfigurationGroup') }),
          }),
      ags: z.array(
        z.number({
          message: t('Validation.SpecifyAssetGroupName'),
        }),
      ),
      desc: z.string().max(DESCRIPTION_MAX, {
        message: t('Validation.DescriptionTooLong', { max: DESCRIPTION_MAX }),
      }),
    });
  }, [isPpu, t]);

  const [triggerEditItem] = useEditAssetMutation();

  const { canSubmit, onSubmit, errorMessage, control } = useModelForm<
    DataModel,
    FormModel,
    EditModel
  >({
    modelName,
    defaultValues,
    item,
    schema,
    serverErrorFields,
    triggerEditItem,
    getEditArgs,
    validateOnLoad: true,
  });

  const configGroupNumberOptions = useConfigGroupNumberOptions();
  const objectTypeOptions = useObjectTypeOptions();
  const assetName = useWatch({ control, name: 'n' });

  return (
    <ModelForm
      canSubmit={canSubmit}
      onSubmit={onSubmit}
      errorMessage={errorMessage}
      control={control}
    >
      <>
        <CasFormInput key="name" control={control} name="n" label={t('Fields.AssetName')} />
        {assetName && assetName.length >= 7 && (
          <div className={styles.warningMessage}>{t('Validation.TofLimitReached')}</div>
        )}
        <CasFormMultiselect<FormModel, number>
          key="ags"
          control={control}
          name="ags"
          label={t('Fields.Filters.AssetGroup')}
          useItemsHook={useAssetGroupOptions}
          disabled={!editMode}
        />
        <CasFormDropdown
          key="cfg"
          control={control}
          name="cfg"
          label={t('Fields.ConfigurationGroup')}
          placeholder={t('Fields.Assign')}
          items={configGroupNumberOptions}
          disabled={!editMode || isPpu}
        />
        <CasFormDropdown
          key="obj"
          control={control}
          name="obj"
          label={t('Fields.Filters.ObjectType')}
          items={objectTypeOptions}
          disabled={!(editMode && item?.editObj === true)}
        />
        <CasFormInput
          key={'desc'}
          control={control}
          name="desc"
          label={t('Fields.Description')}
          element="textarea"
        />
      </>
    </ModelForm>
  );
};

export default AssetForm;
